/**
 * /admin/attempts/:attemptId — the attempt review screen.
 *
 * Everything on this page hangs off one clock. The two recordings play in sync,
 * the timeline underneath spans the whole attempt, and every marker on it — a
 * proctor event, a question being opened, a photo — seeks that clock, so
 * "the tab was switched at 14:32" is one click away from watching it happen.
 *
 * Layout, top to bottom: the headline numbers, the players, the timeline, the
 * panel saying which question was on screen at the playhead, then the
 * per-question grading table, the event log and the photo gallery.
 *
 * **Render discipline.** The clock ticks many times a second, and only the
 * player and the timeline care. Every other section below is a `memo`
 * component taking props that do not change while playing — otherwise the
 * question table and the rendered paper re-render on every tick and the tab
 * stops responding. For the same reason the callbacks handed to those sections
 * are `useCallback`-stable and never include the clock object itself. The one
 * addition to that set is `QuestionFollower`, which has to follow the clock to
 * do its job; it is kept cheap by pre-computing its spans once, and it never
 * writes the selected question, so the table and the paper stay still while the
 * recording plays.
 */
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Clock,
  Maximize2,
  Minimize2,
  Pencil,
  Play,
  TriangleAlert,
  User,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dialog,
  Input,
  Table,
  TableEmpty,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from "@/components/ui";
import { PROCTOR_EVENT_LABEL } from "@exam/shared";
import type { ProctorSeverity } from "@exam/shared";
import { DualPlayer } from "./components/DualPlayer";
import { PhotoGallery } from "./components/PhotoGallery";
import {
  QuestionView,
  describeAnswer,
  describeResponse,
} from "./components/QuestionView";
import { QuestionMeta } from "./components/QuestionMeta";
import {
  QuestionFollower,
  buildQuestionSpans,
} from "./components/QuestionFollower";
import { ErrorNote, FixtureBanner, Loading, PageTitle, Stat } from "./components/Shell";
import { AttemptActions } from "./components/AttemptActions";
import { Timeline } from "./components/Timeline";
import { useSyncedPlayback } from "./components/useSyncedPlayback";
import { fetchAttemptReview, overrideMarks } from "./data/client";
import type { AnswerRow, AttemptReview, TimelineEvent } from "./data/types";
import {
  formatClock,
  formatDateTime,
  formatDuration,
  formatMarks,
  formatOffset,
  formatPercent,
} from "./lib/format";
import { plainSummary } from "./QuestionsPage";
import { useAsync } from "./lib/useAsync";

const SEVERITY_TEXT: Record<ProctorSeverity, string> = {
  critical: "text-violation",
  warn: "text-review",
  info: "text-ink-faint",
};

export function AttemptReviewPage() {
  const { attemptId } = useParams();
  const state = useAsync(() => fetchAttemptReview(attemptId!), [attemptId]);

  return (
    <div className="space-y-4">
      {state.loading ? (
        <Loading label="Loading the attempt…" />
      ) : state.error ? (
        <>
          <PageTitle title="Attempt" />
          <ErrorNote error={state.error} />
        </>
      ) : state.data ? (
        <AttemptReviewBody
          review={state.data}
          onChanged={state.set}
          onReload={state.reload}
        />
      ) : null}
    </div>
  );
}

function AttemptReviewBody({
  review,
  onChanged,
  onReload,
}: {
  review: AttemptReview;
  onChanged: (next: AttemptReview) => void;
  /** After an intervention the whole attempt has moved; refetch it. */
  onReload: () => void;
}) {
  const { toast } = useToast();
  // The timeline starts at `start_ms`, which is negative whenever the phone
  // recorded the setup — pairing, paper check, room scan — before t=0.
  const clock = useSyncedPlayback(review.start_ms, review.duration_ms);
  const { seek } = clock;
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
    review.answers[0]?.question.id ?? null,
  );
  const [overriding, setOverriding] = useState<AnswerRow | null>(null);

  /**
   * Where the follower panel is looking. `null` means it tracks the playhead;
   * a number parks it on one visit, so the reviewer can read a question while
   * the recording keeps running past it.
   */
  const [pinnedSpanIndex, setPinnedSpanIndex] = useState<number | null>(null);

  /**
   * Review mode: the footage gets the window.
   *
   * On the normal page the player is one card among several, so the screen
   * recording — the densest feed and the one you actually read — ends up a few
   * hundred pixels tall with the stats, the timeline and the breakdown all
   * competing for the same screen. Reading what a student typed at that size
   * is guesswork.
   *
   * This is a mode rather than a permanent layout because the two jobs are
   * different: the normal page is for taking in an attempt at a glance, and
   * this is for watching one. Escape leaves.
   */
  const [reviewing, setReviewing] = useState(false);
  /**
   * How much of the width the recording takes in review mode. Draggable,
   * because how much room the footage needs against the question depends on
   * what you are looking for — reading typed code wants one, checking a face
   * against the question wants the other.
   */
  const [splitPct, setSplitPct] = useState(80);

  // "At time T the student was on question N", derived from the visit offsets
  // the API already sends. Built once per attempt: the follower reads it on
  // every clock tick, so nothing in here may be computed per tick.
  const spans = useMemo(
    () => buildQuestionSpans(review.answers, review.duration_ms),
    [review.answers, review.duration_ms],
  );

  const selected = useMemo(
    () => review.answers.find((a) => a.question.id === selectedQuestionId) ?? null,
    [review.answers, selectedQuestionId],
  );

  /**
   * Moving the playhead out of the pinned visit releases the pin.
   *
   * The pin exists for one reason: a row click seeks the video, and without it
   * the next clock tick would drag the panel straight back and the click would
   * look broken. But it was only ever released by pressing the button again,
   * which nobody found — so after a single row click the panel was frozen on
   * that question for the rest of the session. Scrubbing the video, skipping,
   * playing on: none of it moved the panel. It read as "the follower does not
   * work", and functionally it did not.
   *
   * Leaving the pinned visit is an unambiguous statement that the reviewer has
   * moved on, so it is the natural release. Playing through the end of the
   * visit resumes following on its own, and the row click itself is safe
   * because the seek it performs lands inside the very span being pinned.
   */
  useEffect(() => {
    if (pinnedSpanIndex === null) return;
    const span = spans[pinnedSpanIndex];
    if (!span) {
      setPinnedSpanIndex(null);
      return;
    }
    if (clock.timeMs < span.startMs || clock.timeMs >= span.endMs) {
      setPinnedSpanIndex(null);
    }
  }, [clock.timeMs, pinnedSpanIndex, spans]);

  /** Park the follower on the visit of `questionId` that began at `offsetMs`. */
  const pinSpan = useCallback(
    (questionId: string, offsetMs: number) => {
      const index = spans.findIndex(
        (span) => span.questionId === questionId && span.startMs === offsetMs,
      );
      setPinnedSpanIndex(index === -1 ? null : index);
    },
    [spans],
  );

  /**
   * Row click: select the question, jump the video to when the student first
   * opened it, and pin the follower panel to that same visit — following would
   * otherwise drag the panel away on the very next tick and the click would
   * look like it had done nothing.
   */
  const goToQuestion = useCallback(
    (answer: AnswerRow) => {
      setSelectedQuestionId(answer.question.id);
      const first = answer.visit_offsets_ms[0];
      if (first === undefined) return;
      seek(first);
      pinSpan(answer.question.id, first);
    },
    [pinSpan, seek],
  );

  /** Timeline tick click: same pinning, for the visit that was clicked. */
  const selectVisit = useCallback(
    (questionId: string, offsetMs: number) => {
      setSelectedQuestionId(questionId);
      pinSpan(questionId, offsetMs);
    },
    [pinSpan],
  );

  const openOverride = useCallback((answer: AnswerRow) => setOverriding(answer), []);

  // Space plays/pauses from anywhere on the page — the most used key here.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (event.code === "Space") {
        event.preventDefault();
        clock.toggle();
        return;
      }
      // Escape is what every full-window mode is expected to answer to, and
      // there is no browser chrome here to fall back on.
      if (event.code === "Escape") setReviewing(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clock]);

  const follower = (
    <QuestionFollower
      spans={spans}
      timeMs={clock.timeMs}
      pinnedIndex={pinnedSpanIndex}
      onPin={setPinnedSpanIndex}
      onSeek={seek}
    />
  );

  /**
   * Review mode gets the FULL question: the options as the student saw them,
   * which one they chose, which was right, how long they spent and how many
   * times they came back. On the normal page that would duplicate the panel
   * already sitting under the table; in review mode there is nothing else to
   * read, and "what did they actually see" is the question being asked.
   */
  const followerRich = (
    <QuestionFollower
      spans={spans}
      timeMs={clock.timeMs}
      pinnedIndex={pinnedSpanIndex}
      onPin={setPinnedSpanIndex}
      onSeek={seek}
      answers={review.answers}
    />
  );

  if (reviewing) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-app">
        {/* A single slim bar instead of the stats block: in this mode the
            question is "what happened", and a score does not answer it. */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border bg-surface px-3 py-1.5">
          <span className="truncate text-xs font-medium text-ink">
            {review.exam_title}
          </span>
          <span className="truncate text-2xs text-ink-muted">
            {review.student_name}
          </span>
          <Button
            size="sm"
            className="ml-auto"
            icon={<Minimize2 />}
            onClick={() => setReviewing(false)}
          >
            Exit review
          </Button>
        </div>

        {/* The timeline is passed as `below`, so it ends where the recording
            ends rather than running under the side panel — and the panel gets
            the full height instead of being cut off partway down. */}
        <DualPlayer
          tracks={review.tracks}
          clock={clock}
          aside={followerRich}
          below={
            <div className="max-h-[20vh]">
              <Timeline
                startMs={review.start_ms}
                endMs={review.duration_ms}
                tracks={review.tracks}
                events={review.events}
                answers={review.answers}
                timeMs={clock.timeMs}
                onSeek={seek}
                selectedQuestionId={selectedQuestionId}
                onSelectQuestion={selectVisit}
              />
            </div>
          }
          splitPct={splitPct}
          onSplitPct={setSplitPct}
          fill
        />
      </div>
    );
  }

  return (
    <>
      <AttemptHeader review={review} onReload={onReload} />
      <FixtureBanner />
      <HeadlineStats review={review} />

      {/* The only two things that follow the clock. */}
      <Card>
        {/* The question follower rides in the player's right-hand column,
            under the cameras: "what was on screen at this moment" belongs
            beside the footage of that moment. It was previously below the
            timeline, i.e. off the bottom of the screen exactly when it was
            wanted, which is why it went unnoticed entirely. */}
        <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-1.5">
          <p className="text-2xs text-ink-faint">
            Watching the footage? Review mode gives the screen recording the
            whole window.
          </p>
          <Button size="sm" icon={<Maximize2 />} onClick={() => setReviewing(true)}>
            Review mode
          </Button>
        </div>

        <DualPlayer tracks={review.tracks} clock={clock} aside={follower} />

        <div className="border-t border-border">
          <Timeline
            startMs={review.start_ms}
            endMs={review.duration_ms}
            tracks={review.tracks}
            events={review.events}
            answers={review.answers}
            timeMs={clock.timeMs}
            onSeek={seek}
            selectedQuestionId={selectedQuestionId}
            onSelectQuestion={selectVisit}
          />
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)]">
        <QuestionBreakdown
          answers={review.answers}
          selectedQuestionId={selectedQuestionId}
          onSelect={goToQuestion}
        />
        <QuestionPanel
          answer={selected}
          onSeek={seek}
          onAdjust={openOverride}
        />
      </div>

      <EventsCard
        events={review.events}
        byType={review.by_type}
        total={review.total_violations}
        onSeek={seek}
      />

      <PhotosCard photos={review.photos} onSeek={seek} />

      <OverrideDialog
        answer={overriding}
        onClose={() => setOverriding(null)}
        onSubmit={async (marks, note) => {
          if (!overriding) return;
          try {
            const result = await overrideMarks(review.attempt_id, {
              question_id: overriding.question.id,
              marks_awarded: marks,
              note,
            });
            onChanged({
              ...review,
              score: result.score,
              max_score: result.max_score,
              answers: review.answers.map((a) =>
                a.question.id === result.answer.question.id ? result.answer : a,
              ),
            });
            setOverriding(null);
            toast({
              tone: "success",
              title: marks === null ? "Override removed." : "Marks adjusted.",
              description: `Total is now ${formatMarks(result.score)}.`,
            });
          } catch (err) {
            toast({
              tone: "error",
              title: "Could not adjust the marks.",
              description: err instanceof Error ? err.message : undefined,
            });
          }
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Header                                                              */
/* ------------------------------------------------------------------ */

const AttemptHeader = memo(function AttemptHeader({
  review,
  onReload,
}: {
  review: AttemptReview;
  onReload: () => void;
}) {
  return (
    <PageTitle
      title={review.exam_title}
      meta={
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="flex items-center gap-1">
            <User className="size-3.5 text-ink-faint" />
            {review.student_name}
          </span>
          <span>{review.subject_name}</span>
          <Badge tone={review.status === "auto_submitted" ? "review" : "neutral"}>
            {review.status === "auto_submitted" ? "auto-submitted" : review.status}
          </Badge>
          {/* Only shown when it is a retake. Reviewing a second, better sitting
              without knowing it is one reads as improvement that never
              happened. */}
          {review.attempt_no > 1 ? (
            <Badge tone="review">attempt {review.attempt_no}</Badge>
          ) : null}
          {/* A gap in the recording is otherwise indistinguishable from a
              camera that failed. Saying the student dropped out and came back,
              and for how long, is what makes the gap judgeable. */}
          {review.rejoin_count > 0 ? (
            <span className="tabular text-review">
              Left &amp; rejoined {review.rejoin_count}
              {"\u00d7"}
              {review.offline_ms > 0
                ? ` · ${formatDuration(review.offline_ms)} offline`
                : ""}
            </span>
          ) : null}
          {review.extra_time_min !== 0 ? (
            <span className="tabular text-review">
              {review.extra_time_min > 0 ? "+" : ""}
              {review.extra_time_min} min granted
            </span>
          ) : null}
          <Link
            to={`/admin/students/${review.student_id}`}
            className="text-accent hover:underline"
          >
            Progress over time
          </Link>
        </span>
      }
      actions={
        <span className="flex flex-wrap items-center gap-1.5">
          {/* Reset and reopen used to live only on the live monitor, and only
              for a running attempt — so once a sitting ended there was no way
              to act on it anywhere. You decide a sitting is a write-off by
              reviewing it, which is here. */}
          <AttemptActions
            attemptId={review.attempt_id}
            studentId={review.student_id}
            status={review.status}
            extraTimeMin={review.extra_time_min}
            onDone={onReload}
          />
          <Link to="/admin/attempts">
            <Button icon={<ArrowLeft />}>All attempts</Button>
          </Link>
        </span>
      }
    />
  );
});

const HeadlineStats = memo(function HeadlineStats({
  review,
}: {
  review: AttemptReview;
}) {
  const takenMs =
    review.started_at !== null && review.submitted_at !== null
      ? review.submitted_at - review.started_at
      : review.duration_ms;

  const ratio =
    review.max_score && review.max_score > 0
      ? (review.score ?? 0) / review.max_score
      : null;

  return (
    <Card>
      <div className="grid grid-cols-2 divide-x divide-border sm:grid-cols-3 lg:grid-cols-6">
        <Stat
          label="Score"
          value={`${formatMarks(review.score)} / ${formatMarks(review.max_score)}`}
          hint={ratio !== null ? formatPercent(ratio) : undefined}
        />
        <Stat
          label="Time taken"
          value={formatDuration(takenMs)}
          hint={
            review.start_ms < -999
              ? `Recording ${formatOffset(review.start_ms)} → ${formatClock(review.duration_ms)}`
              : `Recording spans ${formatClock(review.duration_ms)}`
          }
          icon={Clock}
        />
        <Stat
          label="Submitted"
          value={review.submitted_at === null ? "—" : formatClock24(review.submitted_at)}
          hint={formatDateTime(review.submitted_at)}
        />
        <Stat
          label="Critical"
          value={review.severity.critical}
          tone={review.severity.critical > 0 ? "violation" : "default"}
          icon={TriangleAlert}
        />
        <Stat
          label="Warnings"
          value={review.severity.warn}
          tone={review.severity.warn > 0 ? "review" : "default"}
        />
        <Stat
          label="Info"
          value={review.severity.info}
          hint={`${review.total_violations} events in total`}
        />
      </div>
    </Card>
  );
});

function formatClock24(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/* ------------------------------------------------------------------ */
/* Per-question table                                                  */
/* ------------------------------------------------------------------ */

const QuestionBreakdown = memo(function QuestionBreakdown({
  answers,
  selectedQuestionId,
  onSelect,
}: {
  answers: AnswerRow[];
  selectedQuestionId: string | null;
  onSelect: (answer: AnswerRow) => void;
}) {
  return (
    <Card className="min-w-0">
      <CardHeader
        title="Per-question breakdown"
        description="Click a row to jump the video to when the student opened it."
      />
      <Table>
        <THead>
          <tr>
            <TH align="right">#</TH>
            <TH>Question</TH>
            <TH>Answer</TH>
            <TH>Correct</TH>
            <TH align="right">Marks</TH>
            <TH align="right">Time</TH>
            <TH align="right">Visits</TH>
            <TH align="right">1st opened</TH>
          </tr>
        </THead>
        <TBody>
          {answers.length === 0 ? (
            <TableEmpty colSpan={8}>This attempt has no questions.</TableEmpty>
          ) : (
            answers.map((answer) => {
              const firstVisit = answer.visit_offsets_ms[0];
              return (
                <TR
                  key={answer.question.id}
                  selected={selectedQuestionId === answer.question.id}
                  className="cursor-pointer"
                  onClick={() => onSelect(answer)}
                >
                  <TD align="right" className="tabular text-ink-faint">
                    {answer.order_idx + 1}
                  </TD>
                  <TD className="w-full max-w-0">
                    <span className="block truncate">
                      {plainSummary(answer.question.body_md)}
                    </span>
                  </TD>
                  <TD
                    className={
                      answer.response === null
                        ? "text-ink-faint"
                        : answer.is_correct
                          ? "text-answered"
                          : "text-violation"
                    }
                  >
                    {describeResponse(answer.response)}
                  </TD>
                  <TD className="text-ink-muted">
                    {describeAnswer(answer.question.correct_answer)}
                  </TD>
                  <TD align="right" className="tabular">
                    {answer.overridden ? (
                      <span className="flex items-center justify-end gap-1">
                        <span className="text-ink-faint line-through">
                          {formatMarks(answer.auto_marks_awarded)}
                        </span>
                        <span className="font-medium text-review">
                          {formatMarks(answer.marks_awarded)}
                        </span>
                      </span>
                    ) : (
                      formatMarks(answer.marks_awarded)
                    )}
                  </TD>
                  <TD align="right" className="tabular text-ink-muted">
                    {formatDuration(answer.time_spent_ms)}
                  </TD>
                  <TD align="right" className="tabular text-ink-muted">
                    {answer.visit_count}
                  </TD>
                  <TD align="right">
                    {firstVisit === undefined ? (
                      <span className="text-ink-faint">—</span>
                    ) : (
                      <span className="tabular inline-flex items-center gap-1 text-ink-muted">
                        <Play className="size-3 text-ink-faint" />
                        {formatClock(firstVisit)}
                      </span>
                    )}
                  </TD>
                </TR>
              );
            })
          )}
        </TBody>
      </Table>
    </Card>
  );
});

/* ------------------------------------------------------------------ */
/* Selected question                                                   */
/* ------------------------------------------------------------------ */

const QuestionPanel = memo(function QuestionPanel({
  answer,
  onSeek,
  onAdjust,
}: {
  answer: AnswerRow | null;
  onSeek: (ms: number) => void;
  onAdjust: (answer: AnswerRow) => void;
}) {
  return (
    <Card className="min-w-0">
      <CardHeader
        title={answer ? `Question ${answer.order_idx + 1}` : "Question"}
        actions={
          answer ? (
            <Button size="sm" icon={<Pencil />} onClick={() => onAdjust(answer)}>
              Adjust marks
            </Button>
          ) : null
        }
      />
      {answer ? (
        <div className="space-y-3 p-4">
          <QuestionMeta question={answer.question} showStatus={false} />

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-border bg-surface-sunken px-3 py-2 text-2xs">
            <span className="text-ink-muted">
              Worth{" "}
              <span className="tabular font-medium text-ink">
                {formatMarks(answer.marks)}
              </span>
              {answer.negative_marks > 0 ? (
                <>
                  {" "}
                  · wrong costs{" "}
                  <span className="tabular font-medium text-ink">
                    −{formatMarks(answer.negative_marks)}
                  </span>
                </>
              ) : null}
            </span>
            <span className="text-ink-muted">
              Awarded{" "}
              <span className="tabular font-medium text-ink">
                {formatMarks(answer.marks_awarded)}
              </span>
            </span>
            <span className="text-ink-muted">
              {formatDuration(answer.time_spent_ms)} over {answer.visit_count}{" "}
              {answer.visit_count === 1 ? "visit" : "visits"}
            </span>
          </div>

          {answer.overridden ? (
            <div className="rounded-md border border-review/30 bg-review-soft px-3 py-2">
              <p className="text-2xs font-medium text-review">
                Marks adjusted manually
              </p>
              <p className="tabular text-2xs text-ink-muted">
                Auto {formatMarks(answer.auto_marks_awarded)} →{" "}
                {formatMarks(answer.marks_override)}
              </p>
              {answer.override_note ? (
                <p className="mt-0.5 text-2xs text-ink">{answer.override_note}</p>
              ) : null}
            </div>
          ) : null}

          {answer.visit_offsets_ms.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-2xs text-ink-muted">Opened at</span>
              {answer.visit_offsets_ms.map((offset, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onSeek(offset)}
                  className="tabular rounded-sm border border-border-strong bg-surface px-1.5 py-0.5 text-2xs text-ink-muted transition-colors hover:bg-surface-sunken hover:text-ink"
                >
                  {formatClock(offset)}
                </button>
              ))}
            </div>
          ) : null}

          <QuestionView
            question={answer.question}
            showAnswer
            answer={{ response: answer.response, is_correct: answer.is_correct }}
          />
        </div>
      ) : (
        <p className="p-4 text-xs text-ink-faint">Select a question from the table.</p>
      )}
    </Card>
  );
});

/* ------------------------------------------------------------------ */
/* Events                                                              */
/* ------------------------------------------------------------------ */

const EventsCard = memo(function EventsCard({
  events,
  byType,
  total,
  onSeek,
}: {
  events: TimelineEvent[];
  byType: AttemptReview["by_type"];
  total: number;
  onSeek: (ms: number) => void;
}) {
  return (
    <Card>
      <CardHeader
        title="Proctor events"
        description={
          total === 0 ? "Nothing was flagged." : `${total} events. Click one to watch it.`
        }
        actions={
          <div className="flex flex-wrap gap-1.5">
            {byType.slice(0, 4).map((entry) => (
              <Badge
                key={entry.type}
                tone={
                  entry.severity === "critical"
                    ? "violation"
                    : entry.severity === "warn"
                      ? "review"
                      : "neutral"
                }
              >
                {PROCTOR_EVENT_LABEL[entry.type]} {entry.count}
              </Badge>
            ))}
          </div>
        }
      />
      <Table>
        <THead>
          <tr>
            <TH align="right">At</TH>
            <TH>Event</TH>
            <TH>Severity</TH>
            <TH>Detail</TH>
          </tr>
        </THead>
        <TBody>
          {events.length === 0 ? (
            <TableEmpty colSpan={4}>No proctor events were recorded.</TableEmpty>
          ) : (
            events.map((event) => (
              <TR
                key={event.id}
                className="cursor-pointer"
                onClick={() => onSeek(event.offset_ms)}
              >
                <TD align="right" className="tabular">
                  {formatOffset(event.offset_ms)}
                </TD>
                <TD className="font-medium">{PROCTOR_EVENT_LABEL[event.type]}</TD>
                <TD className={SEVERITY_TEXT[event.severity]}>{event.severity}</TD>
                <TD className="text-ink-muted">{describeMetadata(event.metadata)}</TD>
              </TR>
            ))
          )}
        </TBody>
      </Table>
    </Card>
  );
});

const PhotosCard = memo(function PhotosCard({
  photos,
  onSeek,
}: {
  photos: AttemptReview["photos"];
  onSeek: (ms: number) => void;
}) {
  return (
    <Card>
      <CardHeader
        title="Photos"
        description="Room scan and paper check before the exam, answer sheets after."
      />
      <PhotoGallery photos={photos} onSeek={onSeek} />
    </Card>
  );
});

/* ------------------------------------------------------------------ */
/* Override                                                            */
/* ------------------------------------------------------------------ */

/**
 * Manual marks correction — for a NAT answer that was right in substance but
 * outside tolerance, or an ambiguous MSQ. The note is the record of why, and
 * the auto-computed value is kept beside it rather than overwritten.
 */
function OverrideDialog({
  answer,
  onClose,
  onSubmit,
}: {
  answer: AnswerRow | null;
  onClose: () => void;
  onSubmit: (marks: number | null, note: string | null) => void | Promise<void>;
}) {
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  // Reset the form whenever a different question is opened.
  useEffect(() => {
    if (!answer) return;
    setValue(
      answer.marks_override !== null
        ? String(answer.marks_override)
        : String(answer.marks_awarded ?? 0),
    );
    setNote(answer.override_note ?? "");
  }, [answer]);

  const submit = async (marks: number | null) => {
    setBusy(true);
    try {
      await onSubmit(marks, note.trim() === "" ? null : note.trim());
    } finally {
      setBusy(false);
    }
  };

  const parsed = Number(value);
  const valid = value.trim() !== "" && Number.isFinite(parsed);

  return (
    <Dialog
      open={answer !== null}
      onClose={onClose}
      title={answer ? `Adjust marks — question ${answer.order_idx + 1}` : "Adjust marks"}
      description="The attempt total is recomputed. The original auto-computed marks are kept."
      footer={
        <>
          {answer?.overridden ? (
            <Button
              variant="ghost"
              className="mr-auto"
              disabled={busy}
              onClick={() => void submit(null)}
            >
              Remove override
            </Button>
          ) : null}
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={busy || !valid}
            onClick={() => void submit(parsed)}
          >
            Save
          </Button>
        </>
      }
    >
      {answer ? (
        <div className="space-y-3">
          <div className="rounded-md border border-border bg-surface-sunken px-3 py-2 text-2xs">
            <p className="text-ink-muted">
              Worth <span className="tabular text-ink">{formatMarks(answer.marks)}</span>
              {answer.negative_marks > 0 ? (
                <>
                  {" "}
                  · negative{" "}
                  <span className="tabular text-ink">
                    −{formatMarks(answer.negative_marks)}
                  </span>
                </>
              ) : null}
              {" · "}auto-scored{" "}
              <span className="tabular text-ink">
                {formatMarks(answer.auto_marks_awarded)}
              </span>
            </p>
            <p className="mt-0.5 text-ink-muted">
              The student answered{" "}
              <span className="tabular text-ink">{describeResponse(answer.response)}</span>
              {"; the key says "}
              <span className="tabular text-ink">
                {describeAnswer(answer.question.correct_answer)}
              </span>
              .
            </p>
          </div>

          <Input
            label="Marks to award"
            type="number"
            step="any"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            hint={`Normally between −${formatMarks(answer.negative_marks)} and ${formatMarks(answer.marks)}, but any value is allowed.`}
          />

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-muted">
              Why (kept with the override)
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Answered 3.33 against a key of 3.34 — rounding, accepted."
              className="w-full rounded-md border border-border-strong bg-surface px-2.5 py-2 text-sm text-ink"
            />
          </label>
        </div>
      ) : null}
    </Dialog>
  );
}

function describeMetadata(metadata: Record<string, unknown> | null): string {
  if (!metadata) return "—";
  const parts = Object.entries(metadata)
    .filter(([, v]) => v !== null && v !== undefined)
    .map(([key, value]) =>
      /_ms$/.test(key) && typeof value === "number"
        ? `${key.replace(/_ms$/, "").replace(/_/g, " ")} ${formatDuration(value)}`
        : `${key.replace(/_/g, " ")} ${String(value)}`,
    );
  return parts.length === 0 ? "—" : parts.join(" · ");
}
